import { useAccessibility } from "../../context/AccessibilityContext/AccessibilityContext";
import Modal from "../Modal/Modal";
import style from './eventmodal.module.scss';

const EventModal = ({ event, onClose }) => {
    const { settings: { contrast } } = useAccessibility();

    if (!event) return null;

    // Accessibility-aware colors
    const backgroundColor = contrast === 'default' ? '#fff' : 'var(--bg-primary)';
    const textColor = contrast === 'default' ? '#231f20' : 'var(--text-primary)';
    const accentColor = contrast === 'default' ? '#1d3e8c' : 'var(--accent-color)';
    const borderColor = contrast === 'default' ? '#e5e5e5' : 'var(--border-color)';

    const imageUrl = event.image 
        ? `${event.image}`
        : null;

    // Форматирование даты из start_date и end_date
    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Получение продолжительности события
    const getEventDuration = () => {
        if (!event.start_date || !event.end_date) return '';
        
        const start = new Date(event.start_date);
        const end = new Date(event.end_date);
        const diffMs = end - start;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (diffHours > 0) {
            return `${diffHours} ч ${diffMinutes > 0 ? `${diffMinutes} мин` : ''}`;
        } else {
            return `${diffMinutes} мин`;
        }
    };

    // Проверка, является ли событие одним днем
    const isSameDay = () => {
        if (!event.start_date || !event.end_date) return true;
        
        const start = new Date(event.start_date);
        const end = new Date(event.end_date);
        return start.toDateString() === end.toDateString();
    };

    return (
        <Modal
            active={!!event}
            setActive={onClose}
            width="90%"
            maxWidth="800px"
            height="fit-content"
            maxHeight="90vh"
            padding="0"
        >
            <div 
                className={style.eventModalContent}
                style={{ 
                    background: backgroundColor,
                    color: textColor
                }}
            >
                {/* Close Button */}
                <button 
                    className={style.closeButton}
                    onClick={onClose}
                    aria-label="Закрыть"
                    style={{
                        color: textColor,
                        border: `2px solid ${borderColor}`
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>

                {/* Header with Image */}
                {imageUrl && (
                    <div className={style.eventHeader}>
                        <div className={style.imageContainer}>
                            <img 
                                src={imageUrl} 
                                alt={event.title} 
                                className={style.eventImage}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                            <div className={style.imageOverlay}></div>
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className={style.eventBody}>
                    {/* Meta Information */}
                    <div className={style.eventMeta}>
                        <span 
                            className={style.eventType}
                            style={{
                                background: accentColor,
                                color: '#fff'
                            }}
                        >
                            📅 Мероприятие
                        </span>
                    </div>

                    {/* Title */}
                    <h1 className={style.eventTitle}>
                        {event.title}
                    </h1>

                    {/* Event Details */}
                    <div className={style.eventDetails}>
                        {/* Дата и время */}
                        {event.start_date && (
                            <div className={style.detailItem}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                                <div className={style.dateTimeInfo}>
                                    <div className={style.dateRange}>
                                        <strong>Когда:</strong> {formatDateTime(event.start_date)}
                                        {event.end_date && !isSameDay() && (
                                            <span className={style.dateTo}>
                                                {' → '}{formatDateTime(event.end_date)}
                                            </span>
                                        )}
                                    </div>
                                    {event.end_date && isSameDay() && (
                                        <div className={style.timeRange}>
                                            <strong>Время:</strong> {new Date(event.start_date).toLocaleTimeString('ru-RU', { 
                                                hour: '2-digit', 
                                                minute: '2-digit' 
                                            })} - {new Date(event.end_date).toLocaleTimeString('ru-RU', { 
                                                hour: '2-digit', 
                                                minute: '2-digit' 
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Продолжительность */}
                        {event.start_date && event.end_date && (
                            <div className={style.detailItem}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                            </div>
                        )}
                    </div>

                    {/* Text Content */}
                    <div className={style.eventText}>
                        <h3 className={style.sectionTitle}>Описание мероприятия</h3>
                        {event.text && event.text.split('\n').map((paragraph, index) => (
                            <p key={index} className={style.eventParagraph}>
                                {paragraph}
                            </p>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EventModal;